import { forwardRef, useEffect, useRef } from "react"

const FADE_START = 100
const FADE_EXIT_BUFFER = 50

export const HeroSection = forwardRef<HTMLElement>((_, ref) => {
  const headingRef = useRef<HTMLHeadingElement>(null)
  const subheadingRef = useRef<HTMLDivElement>(null)
  const copyRef = useRef<HTMLParagraphElement>(null)

  useEffect(() => {
    if (typeof window === "undefined") return
    let raf = 0
    const measure = () => {
      const elements = [headingRef.current, subheadingRef.current, copyRef.current].filter((node): node is HTMLElement => !!node)
      elements.forEach(node => {
        const rect = node.getBoundingClientRect()
        const fadeEnd = -rect.height + FADE_EXIT_BUFFER
        let value = 1
        if (rect.top <= FADE_START) {
          value = rect.top <= fadeEnd ? 0 : (rect.top - fadeEnd) / (FADE_START - fadeEnd)
        }
        node.style.opacity = `${Math.min(1, Math.max(0, value))}`
      })
    }
    const schedule = () => {
      if (raf) return
      raf = window.requestAnimationFrame(() => {
        raf = 0
        measure()
      })
    }
    measure()
    window.addEventListener("scroll", schedule, { passive: true })
    window.addEventListener("resize", schedule)
    return () => {
      if (raf) window.cancelAnimationFrame(raf)
      window.removeEventListener("scroll", schedule)
      window.removeEventListener("resize", schedule)
    }
  }, [])

  return (
    <section ref={ref} data-hero-section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 sm:py-12 md:pb-16">
      <div className="max-w-3xl">
        <h1 ref={headingRef} className="text-4xl font-semibold tracking-tight sm:text-5xl lg:text-6xl">Data Sharing License</h1>
        <h2 className="text-xl font-medium tracking-tight text-primary/80 mt-1">by ADBEx</h2>
        <p ref={copyRef} className="mt-6 text-base leading-7 text-muted-foreground sm:text-lg">
          Craft clear, machine-readable licenses for your datasets in minutes. Start with sensible presets, customize the terms that matter (attribution, commercial use, derivatives, jurisdiction), and export human- and machine-friendly outputs ready for your repository, website, or API.
        </p>
      </div>
    </section>
  )
})

HeroSection.displayName = "HeroSection"
